import HomeTableHeader from "../HomeTableHeader/HomeTableHeader";
import React, { FC, useEffect, useState } from "react";
import { IHomeFilter } from "../../common/interfaces/home.interface";
import { Button, MenuItem, Stack, Select, FormControl, InputLabel } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";

interface TableHeaderProps {
  performSearch: () => Promise<void>;
  setSearchFilters: React.Dispatch<React.SetStateAction<IHomeFilter>>;
  triggerFilterClear: string;
}

const CentralRegistryTableHeader: FC<TableHeaderProps> = ({ performSearch, setSearchFilters, triggerFilterClear }) => {
  const [conservationValue, setConservationValue] = useState<boolean | null>(null);
  const [digitizationValue, setDigitizationValue] = useState<boolean | null>(null);
  const [microfilmValue, setMicrofilmValue] = useState<boolean | null>(null);
  const currentYear = dayjs();
  const [isBibliographicUnitSelected, setIsBibliographicUnitSelected] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [dateOfRegistration, setDateOfRegistration] = useState<dayjs.Dayjs | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 500);

    const savedFilters = JSON.parse(localStorage.getItem("searchFilters") || "{}");
    setMicrofilmValue(savedFilters["hasMicrofilm"]);
    setConservationValue(savedFilters["hasConservation"]);
    setDigitizationValue(savedFilters["hasDigitalization"]);

    if (savedFilters["dateOfRegistration"]) {
      const savedDate = savedFilters["dateOfRegistration"][0];
      setDateOfRegistration(savedDate ? dayjs(savedDate) : null);
    }

    return () => clearTimeout(timer);
  }, []);

  const handleSearchChange = (field: keyof IHomeFilter | any, value: any) => {
    if (isReady) {
      setSearchFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters };

        if (value === null || (Array.isArray(value) && value.length === 0)) {
          // @ts-ignore
          delete updatedFilters[field];
        } else {
          // @ts-ignore
          updatedFilters[field] = value;
        }
        localStorage.setItem("searchFilters", JSON.stringify(updatedFilters));
        return updatedFilters;
      });
    }
  };

  const handleDateChange = (value: any) => {
    if (value) {
      const formattedDate = dayjs(value).format("YYYY-MM");
      handleSearchChange("dateOfRegistration", [formattedDate]);
    } else {
      handleSearchChange("dateOfRegistration", []);
    }
  };

  const handleChange = (
    field: "hasConservation" | "hasDigitalization" | "hasMicrofilm",
    value: boolean | null
  ) => {
    switch (field) {
      case "hasConservation":
        setConservationValue(value);
        handleSearchChange("hasConservation", value);
        break;
      case "hasDigitalization":
        setDigitizationValue(value);
        handleSearchChange("hasDigitalization", value);
        break;
      case "hasMicrofilm":
        setMicrofilmValue(value);
        handleSearchChange("hasMicrofilm", value);
        break;
      default:
        break;
    }
  };

  const [resetKey, setResetKey] = useState(0);
  const handleResetFilters = () => {
    localStorage.setItem("searchFilters", JSON.stringify({}));
    setSearchFilters({});
    setDigitizationValue(null);
    setConservationValue(null);
    setMicrofilmValue(null);
    setIsBibliographicUnitSelected(false);
    setDateOfRegistration(null);
    setResetKey((prev) => prev + 1);
  };

  useEffect(() => {
    if ('' !== triggerFilterClear) {
      handleResetFilters()
    }
  }, [triggerFilterClear]);

  return (
    <>
      <HomeTableHeader
        getCulturalProperties={async () => {}}
        setSearchFilters={setSearchFilters}
        isCentralRegistry
        setIsBibliographicUnitSelected={setIsBibliographicUnitSelected}
        setResetKey={setResetKey}
        resetKey={resetKey}
      />
      <Stack className="filter-section" direction="row" spacing={1} mb={1}>
        <FormControl sx={{ width: "25%" }}>
          <InputLabel>Конзервација</InputLabel>
          <Select
            disabled={isBibliographicUnitSelected}
            label="Конзервација"
            value={conservationValue ?? ""}
            onChange={(event) => {
              const selectedValue = event.target.value;
              if (selectedValue === conservationValue?.toString()) {
                handleChange("hasConservation", null);
              } else {
                handleChange(
                  "hasConservation",
                  event.target.value === "null" ? null : event.target.value === "true"
                );
              }
            }}
          >
            <MenuItem value="true">Извршена</MenuItem>
            <MenuItem value="false">Није извршена</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ width: "25%" }}>
          <InputLabel>Дигитализација</InputLabel>
          <Select
            disabled={isBibliographicUnitSelected}
            label="Дигитализација"
            value={digitizationValue ?? ""}
            onChange={(event) => {
              const selectedValue = event.target.value;
              if (selectedValue === digitizationValue?.toString()) {
                handleChange("hasDigitalization", null);
              } else {
                handleChange(
                  "hasDigitalization",
                  event.target.value === "null" ? null : event.target.value === "true"
                );
              }
            }}
          >
            <MenuItem value="true">Постоји дигитална копија</MenuItem>
            <MenuItem value="false">Не постоји дигитална копија</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ width: "25%" }}>
          <InputLabel>Микрофилм</InputLabel>
          <Select
            disabled={isBibliographicUnitSelected}
            value={microfilmValue ?? ""}
            label="Микрофилм"
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue === microfilmValue?.toString()) {
                handleChange("hasMicrofilm", null);
              } else {
                handleChange(
                  "hasMicrofilm",
                  selectedValue === "true" ? true : selectedValue === "false" ? false : null
                );
              }
            }}
          >
            <MenuItem value="true">Постоји микрофилм</MenuItem>
            <MenuItem value="false">Не постоји микрофилм</MenuItem>
          </Select>
        </FormControl>

        <DatePicker
          key={`dateKey-${resetKey}`}
          label="Датум регистрације"
          openTo="year"
          format="YYYY-MM"
          views={["year", "month"]}
          yearsOrder="desc"
          sx={{ width: "25%" }}
          maxDate={currentYear}
          value={dateOfRegistration}
          onChange={(newValue) => {
            setDateOfRegistration(newValue);
            handleDateChange(newValue);
          }}
          slotProps={{ field: { clearable: true } }}
        />

        <Button variant="contained" onClick={performSearch}>
          <SearchIcon />
        </Button>
        <Button variant="contained" onClick={handleResetFilters}>
          <CloseIcon />
        </Button>
      </Stack>
    </>
  );
};

export default CentralRegistryTableHeader;
